import { Typography } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import { alpha, styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useContext } from 'react';
import Highlighter from 'react-highlight-words';
import { SearchIcon } from '../../../assets/icon/IconCollection';
import { NavigationMenuContext } from '../../../store/side-menu-navigation-context';
import { MenuItem } from '../side-menu/MenuData';

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '100%',
    fontStyle: 'normal'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-input::placeholder': {
        fontStyle: 'normal',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        borderRadius: 3,
        border: '1px solid #C4C6C8',
        transition: 'none', // Remove transition effect on width
        width: '100%', // Ensure width is 100%
    },
}));

export interface SearchbarProps {
    placeholder?: string;
    icon?: React.ReactNode;
    onSearch: (query: string) => void;
    searchResult: MenuItem[];
    onMenuClick: (item: MenuItem | null) => void;
}

const Searchbar: React.FC<SearchbarProps> = ({ placeholder = "Find Anything", icon, onSearch, searchResult, onMenuClick }) => {
    const { handleNavigationChange, clearMenuSelection } = useContext(NavigationMenuContext);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<MenuItem[]>([]);
    const [selectedValue, setSelectedValue] = React.useState<MenuItem | null>(null);

    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    return (
        <Search sx={{ backgroundColor: 'transparent', borderRadius: 2 }}>
            <Stack spacing={2}>
                <Autocomplete
                    size='small'
                    ListboxProps={{
                        sx: {
                            maxHeight: 400,
                            overflowY: 'auto'
                        }
                    }}
                    autoHighlight
                    id="navigation-search"
                    sx={{ width: 500 }}
                    freeSolo
                    filterOptions={(x) => inputValue?x:[]}
                    options={searchResult}
                    autoComplete
                    filterSelectedOptions
                    getOptionLabel={(option) => option.title || ''}
                    inputValue={inputValue}
                    value={selectedValue}
                    onChange={(event: any, newValue: MenuItem | null) => {
                        setOptions(newValue ? [newValue, ...options] : options);
                        setSelectedValue(null);
                        setInputValue('');
                        if (newValue && newValue.title !== "No Records found!") {
                            onMenuClick(newValue);
                            if (newValue?.path) {
                                clearMenuSelection();
                                let level = 'root';
                                let panel = 'root'
                                newValue.path.split('/').map((path, index) => {
                                    panel = panel + '-' + path;
                                    handleNavigationChange(level, panel);
                                    level = level + '-' + path;
                                });
                            }
                        }
                    }}
                    onInputChange={(event, newInputValue) => {
                        onSearch(newInputValue);
                        setInputValue(newInputValue);
                    }}
                    renderInput={(params) => (
                        <StyledTextField {...params} fullWidth InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <SearchIcon />
                            )
                        }} placeholder='Search' />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        let path = option.path ? option.path.split("/") : [];
                        if (path) {
                            if (path.length >= 3 && path[2] === "Login") {
                                path.pop();
                            } else {
                                path.shift();
                            }
                        }
                        return (
                            <li key={`${option.title}-${option.path}`} {...optionProps}>
                                {
                                    <Typography
                                        className={`list-item-text ${option.title === "Overview" ? "overview-item" : ""
                                            }`}
                                    >
                                        {option.title.startsWith("GET") ||
                                            option.title.startsWith("PUT") ||
                                            option.title.startsWith("POST") ||
                                            option.title.startsWith("DEL") ? (
                                            <>
                                                <span
                                                    className="search-result-method-word"
                                                    style={{ color: option.color || "inherit" }}
                                                >
                                                    <Highlighter
                                                        highlightClassName="navigation-search-highligher"
                                                        searchWords={[escapeRegExp(inputValue)]}
                                                        autoEscape={true}
                                                        textToHighlight={option.title.split(" ")[0] + " "}
                                                    />
                                                </span>
                                                <span className="search-result-method-description">
                                                    <Highlighter
                                                        highlightClassName="navigation-search-highligher"
                                                        searchWords={[escapeRegExp(inputValue)]}
                                                        textToHighlight={option.title.split(" ").slice(1).join(" ")}
                                                    />
                                                </span>
                                            </>
                                        ) : (
                                            <Highlighter
                                                highlightClassName="navigation-search-highligher"
                                                searchWords={[escapeRegExp(inputValue)]}
                                                textToHighlight={option.title}
                                            />
                                        )}
                                        {path && path.length > 0 ? <span style={{ color: '#6B7075', fontSize: 12, fontWeight: 400 }} > from {path.join(' > ')}</span> : ""}
                                    </Typography>
                                }
                            </li>
                        );
                    }}
                />
            </Stack>
        </Search>
    );
};

export default Searchbar;
